import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DollarSign, Wallet, Clock, TrendingUp, TrendingDown, ArrowUpRight,
  Download, Calendar as CalendarIcon, Sparkles, Trophy, Package, MapPin,
} from "lucide-react";
import { format, differenceInDays, subDays, subMonths } from "date-fns";
import type { DateRange } from "react-day-picker";
import { toast } from "sonner";

import { MerchantShell } from "@/components/merchant/merchant-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/merchant/revenue")({
  head: () => ({
    meta: [
      { title: "Revenue — Spayzo Merchant" },
      { name: "description", content: "Track earnings, growth, and revenue by space." },
    ],
  }),
  component: RevenuePage,
});

const trend12m = [
  { m: "Jan", v: 4200 }, { m: "Feb", v: 5100 }, { m: "Mar", v: 4800 },
  { m: "Apr", v: 6200 }, { m: "May", v: 7400 }, { m: "Jun", v: 6900 },
  { m: "Jul", v: 8200 }, { m: "Aug", v: 9100 }, { m: "Sep", v: 8700 },
  { m: "Oct", v: 10400 }, { m: "Nov", v: 11200 }, { m: "Dec", v: 12800 },
];
const trend90d = [
  { m: "W1", v: 2100 }, { m: "W2", v: 2600 }, { m: "W3", v: 2400 },
  { m: "W4", v: 3100 }, { m: "W5", v: 2900 }, { m: "W6", v: 3400 },
  { m: "W7", v: 3200 }, { m: "W8", v: 3800 }, { m: "W9", v: 3600 },
  { m: "W10", v: 4200 }, { m: "W11", v: 4500 }, { m: "W12", v: 4900 },
];
const trend30d = [
  { m: "D1", v: 380 }, { m: "D5", v: 520 }, { m: "D9", v: 470 },
  { m: "D13", v: 610 }, { m: "D17", v: 690 }, { m: "D21", v: 640 },
  { m: "D25", v: 780 }, { m: "D30", v: 860 },
];

const spaces = [
  { nameKey: "merchantRevenue.space.loftStudio", revenue: 18420, bookings: 84, growth: 12.4, color: "bg-primary" },
  { nameKey: "merchantRevenue.space.rooftop", revenue: 14210, bookings: 62, growth: 8.1, color: "bg-emerald-500" },
  { nameKey: "merchantRevenue.space.podcastBooth", revenue: 9840, bookings: 124, growth: -3.2, color: "bg-amber-500" },
  { nameKey: "merchantRevenue.space.coworkLoft", revenue: 7620, bookings: 41, growth: 21.7, color: "bg-sky-500" },
  { nameKey: "merchantRevenue.space.photoStudio", revenue: 5380, bookings: 28, growth: 4.6, color: "bg-violet-500" },
];

const products = [
  { nameKey: "merchantRevenue.product.studioGear", revenue: 4820, share: 38 },
  { nameKey: "merchantRevenue.product.catering", revenue: 3210, share: 25 },
  { nameKey: "merchantRevenue.product.cleaning", revenue: 2140, share: 17 },
  { nameKey: "merchantRevenue.product.parking", revenue: 1480, share: 12 },
  { nameKey: "merchantRevenue.product.lateHour", revenue: 1010, share: 8 },
];



function Kpi({ icon: Icon, label, value, sub, trend: t, tone = "default" }: {
  icon: React.ComponentType<{ className?: string }>; label: string; value: React.ReactNode; sub?: string;
  trend?: { v: string; up: boolean }; tone?: "default" | "primary" | "amber";
}) {
  return (
    <Card className="rounded-2xl border-border/60 p-5 shadow-soft transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <span className={cn("grid h-10 w-10 place-items-center rounded-xl",
          tone === "primary" ? "bg-[#5958F3] text-white" :
          tone === "amber" ? "bg-amber-500/15 text-amber-600" : "bg-[#5958F3]/10 text-[#5958F3]")}>
          <Icon className="h-5 w-5" />
        </span>
        {t && (
          <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
            t.up ? "bg-emerald-500/10 text-emerald-700" : "bg-rose-500/10 text-rose-700")}>
            {t.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />} {t.v}
          </span>
        )}
      </div>
      <p className="mt-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-3xl font-bold tracking-tight">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}

function RevenuePage() {
  const { t } = useTranslation();
  const rangeLabel: Record<"30d" | "90d" | "12m", string> = {
    "30d": t("merchantRevenue.range.30d"),
    "90d": t("merchantRevenue.range.90d"),
    "12m": t("merchantRevenue.range.12m"),
  };
  const [range, setRange] = useState<"30d" | "90d" | "12m">("12m");
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const captureRef = useRef<HTMLDivElement | null>(null);

  const trend = range === "30d" ? trend30d : range === "90d" ? trend90d : trend12m;
  const total = useMemo(() => trend.reduce((a, b) => a + b.v, 0), [trend]);
  const prev = Math.round(total * 0.86);
  const delta = ((total - prev) / prev) * 100;
  const max = useMemo(() => Math.max(...trend.map((t) => t.v)), [trend]);
  const topSpaceMax = Math.max(...spaces.map((s) => s.revenue));

  const applyDateRange = (r: DateRange | undefined) => {
    setDateRange(r);
    if (r?.from && r?.to) {
      const days = differenceInDays(r.to, r.from) + 1;
      const next = days <= 45 ? "30d" : days <= 150 ? "90d" : "12m";
      setRange(next);
      setDatePopoverOpen(false);
      toast.success(t("merchantRevenue.toast.range", { from: format(r.from, "MMM d, yyyy"), to: format(r.to, "MMM d, yyyy") }));
    }
  };

  const dateLabel = dateRange?.from && dateRange?.to
    ? `${format(dateRange.from, "MMM d")} — ${format(dateRange.to, "MMM d, yyyy")}`
    : rangeLabel[range];

  const handleExport = async () => {
    if (!captureRef.current) return;
    setExporting(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const ratio = canvas.width / canvas.height;
      let w = pageW - 40;
      let h = w / ratio;
      if (h > pageH - 40) { h = pageH - 40; w = h * ratio; }
      pdf.addImage(img, "PNG", (pageW - w) / 2, 20, w, h);
      pdf.save(`revenue-${range}-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(t("merchantRevenue.toast.exported"));
    } catch (e) {
      toast.error(t("merchantRevenue.toast.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <MerchantShell
      title={t("merchantRevenue.title")}
      subtitle={t("merchantRevenue.subtitle")}
      actions={
        <>
          <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-10 rounded-xl gap-2">
                <CalendarIcon className="h-4 w-4" /> {dateLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0 pointer-events-auto">
              <div className="flex flex-col sm:flex-row">
                <div className="flex flex-col gap-1 border-b sm:border-b-0 sm:border-e border-border/60 p-3 text-sm">
                  {[
                    { label: t("merchantRevenue.quickRange.last30"), get: () => ({ from: subDays(new Date(), 29), to: new Date() }) },
                    { label: t("merchantRevenue.quickRange.last90"), get: () => ({ from: subDays(new Date(), 89), to: new Date() }) },
                    { label: t("merchantRevenue.quickRange.last12m"), get: () => ({ from: subMonths(new Date(), 12), to: new Date() }) },
                    { label: t("merchantRevenue.quickRange.ytd"), get: () => ({ from: new Date(new Date().getFullYear(), 0, 1), to: new Date() }) },
                  ].map((p) => (
                    <button
                      key={p.label}
                      onClick={() => applyDateRange(p.get())}
                      className="rounded-md px-3 py-1.5 text-start hover:bg-muted"
                    >
                      {p.label}
                    </button>
                  ))}
                  <Separator className="my-1" />
                  <button
                    onClick={() => { setDateRange(undefined); setDatePopoverOpen(false); toast.success(t("merchantRevenue.rangeCleared")); }}
                    className="rounded-md px-3 py-1.5 text-start text-muted-foreground hover:bg-muted"
                  >
                    {t("merchantRevenue.clear")}
                  </button>
                </div>
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={applyDateRange}
                  numberOfMonths={2}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </div>
            </PopoverContent>
          </Popover>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="h-10 rounded-xl bg-[#5958F3] text-white shadow-soft hover:bg-[#5958F3]/90 gap-2"
          >
            <Download className="h-4 w-4" /> {exporting ? t("merchantRevenue.exporting") : t("merchantRevenue.exportPdf")}
          </Button>
        </>
      }
    >
      <div ref={captureRef} className="space-y-6 bg-background">
        {/* KPI cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Kpi icon={DollarSign} label={t("merchantRevenue.kpi.totalEarnings")} value={<Money value={(total).toLocaleString()} />} sub={t("merchantRevenue.kpi.netOfFees", { range: rangeLabel[range] })}
            trend={{ v: `${delta >= 0 ? "+" : ""}${delta.toFixed(1)}%`, up: delta >= 0 }} tone="primary" />
          <Kpi icon={Wallet} label={t("merchantRevenue.kpi.avgPerPeriod")} value={<Money value={Math.round(total / trend.length).toLocaleString()} />} sub={t("merchantRevenue.kpi.dataPoints", { count: trend.length })}
            trend={{ v: "+9.4%", up: true }} />
          <Kpi icon={Clock} label={t("merchantRevenue.kpi.pendingPayouts")} value={<Money value="3,210" />} sub={t("merchantRevenue.kpi.settling")}
            tone="amber" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft lg:col-span-2">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("merchantRevenue.revenueTrend")}</p>
                <p className="mt-1 font-display text-xl font-bold tracking-tight">{t("merchantRevenue.earned", { amount: `SAR ${total.toLocaleString()}` })}</p>
                <p className="text-xs text-muted-foreground">{t("merchantRevenue.vsPrevious", { amount: `SAR ${prev.toLocaleString()}` })}</p>
              </div>
              <Tabs value={range} onValueChange={(v) => setRange(v as typeof range)}>
                <TabsList className="rounded-xl">
                  <TabsTrigger value="30d" className="rounded-lg">{t("merchantRevenue.tab.30d")}</TabsTrigger>
                  <TabsTrigger value="90d" className="rounded-lg">{t("merchantRevenue.tab.90d")}</TabsTrigger>
                  <TabsTrigger value="12m" className="rounded-lg">{t("merchantRevenue.tab.12m")}</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            {/* Line/area chart */}
            <div className="mt-5">
              <svg viewBox="0 0 600 200" className="h-56 w-full" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="rev-grad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#5958F3" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#5958F3" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[0, 1, 2, 3].map((i) => (
                  <line key={i} x1="0" x2="600" y1={40 + i * 40} y2={40 + i * 40}
                    stroke="hsl(var(--border))" strokeDasharray="3 4" />
                ))}
                {(() => {
                  const pts = trend.map((t, i) => {
                    const x = (i / Math.max(1, trend.length - 1)) * 600;
                    const y = 190 - (t.v / max) * 160;
                    return [x, y] as const;
                  });
                  const path = pts.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
                  const area = `${path} L600,200 L0,200 Z`;
                  return (
                    <>
                      <path d={area} fill="url(#rev-grad)" />
                      <path d={path} fill="none" stroke="#5958F3" strokeWidth="2.5" strokeLinejoin="round" />
                      {pts.map(([x, y], i) => (
                        <circle key={i} cx={x} cy={y} r="3.5" fill="#ffffff" stroke="#5958F3" strokeWidth="2" />
                      ))}
                    </>
                  );
                })()}
              </svg>
              <div className="mt-2 flex justify-between text-[10px] font-medium text-muted-foreground">
                {trend.map((t) => <span key={t.m}>{t.m}</span>)}
              </div>
            </div>
          </Card>

          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("merchantRevenue.earningsBySpace")}</p>
            <p className="mt-1 font-display text-xl font-bold tracking-tight">{t("merchantRevenue.top5")}</p>
            <div className="mt-4 space-y-4">
              {spaces.map((s) => (
                <div key={s.nameKey}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="truncate font-medium">{t(s.nameKey)}</span>
                    <span className="font-semibold tabular-nums"><Riyal /> {s.revenue.toLocaleString()}</span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted">
                    <div className={cn("h-full rounded-full", s.color)}
                      style={{ width: `${(s.revenue / topSpaceMax) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Breakdown */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card className="overflow-hidden rounded-2xl border-border/60 shadow-soft">
            <div className="flex items-center justify-between p-5">
              <div className="flex items-center gap-2.5">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#5958F3]/10 text-[#5958F3]">
                  <MapPin className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-display text-base font-semibold">{t("merchantRevenue.perSpaceEarnings")}</p>
                  <p className="text-xs text-muted-foreground">{t("merchantRevenue.bookingsRevenueGrowth")}</p>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto border-t border-border/60">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="p-3 text-start">{t("merchantRevenue.table.space")}</th>
                    <th className="p-3 text-end">{t("merchantRevenue.table.bookings")}</th>
                    <th className="p-3 text-end">{t("merchantRevenue.table.revenue")}</th>
                    <th className="p-3 text-end">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {spaces.map((s) => (
                    <tr key={s.nameKey} className="border-t border-border/60 hover:bg-muted/40">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className={cn("h-2 w-2 rounded-full", s.color)} />
                          <span className="truncate font-medium">{t(s.nameKey)}</span>
                        </div>
                      </td>
                      <td className="p-3 text-end tabular-nums">{s.bookings}</td>
                      <td className="p-3 text-end font-semibold tabular-nums"><Riyal /> {s.revenue.toLocaleString()}</td>
                      <td className="p-3 text-end">
                        <span className={cn("inline-flex items-center gap-0.5 text-xs font-semibold",
                          s.growth >= 0 ? "text-emerald-700" : "text-rose-700")}>
                          {s.growth >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                          {Math.abs(s.growth)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#5958F3]/10 text-[#5958F3]">
                <Package className="h-4 w-4" />
              </span>
              <div>
                <p className="font-display text-base font-semibold">{t("merchantRevenue.productRevenueContribution")}</p>
                <p className="text-xs text-muted-foreground">{t("merchantRevenue.addonsRentalsMonth")}</p>
              </div>
            </div>
            <div className="mt-5 flex items-center gap-6">
              {/* Donut */}
              <div className="relative h-36 w-36 shrink-0">
                <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
                  {(() => {
                    const colors = ["#5958F3", "#10b981", "#f59e0b", "#0ea5e9", "#8b5cf6"];
                    let acc = 0;
                    return products.map((p, i) => {
                      const dash = p.share;
                      const gap = 100 - dash;
                      const off = -acc;
                      acc += dash;
                      return (
                        <circle key={p.nameKey} cx="18" cy="18" r="15.9155"
                          fill="transparent" stroke={colors[i]} strokeWidth="4"
                          strokeDasharray={`${dash} ${gap}`} strokeDashoffset={off} />
                      );
                    });
                  })()}
                </svg>
                <div className="absolute inset-0 grid place-items-center">
                  <div className="text-center">
                    <p className="text-[10px] font-medium uppercase text-muted-foreground">{t("merchantRevenue.total")}</p>
                    <p className="font-display text-lg font-bold"><Riyal /> 12.6k</p>
                  </div>
                </div>
              </div>
              <div className="flex-1 space-y-2">
                {products.map((p, i) => {
                  const colors = ["bg-[#5958F3]", "bg-emerald-500", "bg-amber-500", "bg-sky-500", "bg-violet-500"];
                  return (
                    <div key={p.nameKey} className="flex items-center gap-2 text-xs">
                      <span className={cn("h-2 w-2 rounded-full", colors[i])} />
                      <span className="flex-1 truncate">{t(p.nameKey)}</span>
                      <span className="font-semibold tabular-nums">{p.share}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>

        {/* Insights */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft lg:col-span-2 bg-gradient-to-br from-[#5958F3]/10 via-background to-background">
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#5958F3] text-white">
                <Trophy className="h-4 w-4" />
              </span>
              <div>
                <p className="font-display text-base font-semibold">{t("merchantRevenue.bestPerformingSpaces")}</p>
                <p className="text-xs text-muted-foreground">{t("merchantRevenue.highestGrowthQuarter")}</p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {spaces.slice(0, 3).map((s, i) => (
                <div key={s.nameKey} className="rounded-xl border border-border/60 bg-background p-4">
                  <div className="flex items-center justify-between">
                    <span className={cn("grid h-7 w-7 place-items-center rounded-lg text-xs font-bold",
                      i === 0 ? "bg-amber-500/15 text-amber-600" :
                      i === 1 ? "bg-slate-400/15 text-slate-600" :
                      "bg-orange-700/15 text-orange-700")}>
                      {i + 1}
                    </span>
                    <Badge className="rounded-full bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10">
                      <TrendingUp className="h-3 w-3" /> {s.growth}%
                    </Badge>
                  </div>
                  <p className="mt-3 truncate text-sm font-semibold">{t(s.nameKey)}</p>
                  <p className="font-display text-xl font-bold tracking-tight"><Riyal /> {s.revenue.toLocaleString()}</p>
                  <p className="text-[11px] text-muted-foreground">{t("merchantRevenue.bookingsCount", { count: s.bookings })}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-500/15 text-emerald-600">
                <Sparkles className="h-4 w-4" />
              </span>
              <div>
                <p className="font-display text-base font-semibold">{t("merchantRevenue.revenueGrowth")}</p>
                <p className="text-xs text-muted-foreground">{t("merchantRevenue.vsPreviousRange", { range: rangeLabel[range].toLowerCase() })}</p>
              </div>
            </div>
            <div className="mt-5">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-4xl font-bold tracking-tight">{delta >= 0 ? "+" : ""}{delta.toFixed(1)}%</span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                  <ArrowUpRight className="h-3 w-3" /><Riyal /> {(total - prev).toLocaleString()}
                </span>
              </div>
              <Progress value={Math.min(100, Math.round((total / 115000) * 100))} className="mt-4 h-2" />
              <p className="mt-2 text-xs text-muted-foreground">{t("merchantRevenue.towardGoal", { percent: Math.min(100, Math.round((total / 115000) * 100)) })}</p>
            </div>
            <div className="mt-5 space-y-2 border-t border-border/60 pt-4 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("merchantRevenue.avgBookingValue")}</span><span className="font-semibold"><Riyal /> 184</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("merchantRevenue.repeatCustomers")}</span><span className="font-semibold">42%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("merchantRevenue.addonAttachRate")}</span><span className="font-semibold">61%</span></div>
            </div>
          </Card>
        </div>
      </div>
    </MerchantShell>
  );
}
